import creditCardType from "credit-card-type";
import visaLogo from "../../assets/images/logo/visa.svg";
import mastercardLogo from "../../assets/images/logo/mastercard.svg";
import amexLogo from "../../assets/images/logo/amex.svg";
import discoverLogo from "../../assets/images/logo/discover.svg";
import dinersLogo from "../../assets/images/logo/diners.svg";
import jcbLogo from "../../assets/images/logo/jcb.svg";

/**
 * Shared DealerCards helpers + Square-like brand badges.
 */

/** Max saved payment cards per dealer — change here to adjust everywhere. */
export const MAX_SAVED_CARDS = 5;

/**
 * Max years ahead we accept client-side.
 * Square docs do not publish a hard ceiling (sandbox success path accepts
 * any future month/year; sandbox 01/40 is a CreatePayment error-injection
 * value, not a year limit). Cap at 20 from typical issuer windows
 * (usually 2–5 years; payment forms commonly allow ~10–20).
 */
export const MAX_CARD_EXPIRATION_YEARS_AHEAD = 20;

export const CARD_EXPIRATION_ERROR_MESSAGE = "Invalid expiration date.";
export const CARD_EXPIRED_ERROR_MESSAGE = "This card has expired.";

/**
 * Normalize YY → YYYY (assume 2000–2099). Leave YYYY as-is.
 */
export function normalizeCardExpYear(expYear) {
  const raw = String(expYear ?? "").trim();
  if (!raw) return null;
  const y = Number(raw);
  if (!Number.isFinite(y) || y <= 0) return null;
  if (y < 100) return 2000 + Math.trunc(y);
  return Math.trunc(y);
}

/**
 * Parse Square-style MM/YY (also accepts MMYY, MM-YY, MM/YYYY).
 * @returns {{ month: number, year: number } | null}
 */
export function parseCardExpiration(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length < 3) return null;

  let month;
  let yearPart;
  if (digits.length === 3) {
    // MY Y → M / YY (rare)
    month = Number(digits.slice(0, 1));
    yearPart = digits.slice(1);
  } else if (digits.length === 4) {
    // MMYY
    month = Number(digits.slice(0, 2));
    yearPart = digits.slice(2);
  } else if (digits.length >= 5 && digits.length <= 6) {
    // MMYYYY
    month = Number(digits.slice(0, 2));
    yearPart = digits.slice(2);
  } else {
    return null;
  }

  const year = normalizeCardExpYear(yearPart);
  if (!Number.isFinite(month) || month < 1 || month > 12 || year == null) {
    return null;
  }
  return { month, year };
}

/** Format digits as MM/YY while typing. */
export function formatCardExpirationInput(value) {
  const digits = String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

/**
 * Reject past expirations and far-future years that are almost certainly
 * typos (Square tokenization may still accept them).
 */
export function isCardExpirationAcceptable(expMonth, expYear) {
  const month = Number(expMonth);
  const year = normalizeCardExpYear(expYear);
  if (!Number.isFinite(month) || month < 1 || month > 12 || year == null) {
    return false;
  }

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  if (year < currentYear || (year === currentYear && month < currentMonth)) {
    return false;
  }
  if (year > currentYear + MAX_CARD_EXPIRATION_YEARS_AHEAD) {
    return false;
  }
  return true;
}

/** Why expiration failed — for clearer UI errors. */
export function getCardExpirationError(expMonth, expYear) {
  const month = Number(expMonth);
  const year = normalizeCardExpYear(expYear);
  if (!Number.isFinite(month) || month < 1 || month > 12 || year == null) {
    return CARD_EXPIRATION_ERROR_MESSAGE;
  }
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  if (year < currentYear || (year === currentYear && month < currentMonth)) {
    return CARD_EXPIRED_ERROR_MESSAGE;
  }
  if (year > currentYear + MAX_CARD_EXPIRATION_YEARS_AHEAD) {
    return CARD_EXPIRATION_ERROR_MESSAGE;
  }
  return null;
}

export function parseIsDefault(value) {
  if (value === true || value === 1 || value === "1") return true;
  if (value === false || value === 0 || value === "0") return false;
  const s = String(value ?? "")
    .trim()
    .toLowerCase();
  return s === "yes" || s === "true" || s === "y" || s === "default";
}

/** Last 4 digits from masked PAN (xxxxxxxxxxxx1128) or raw last4. */
export function last4FromCardNumber(number) {
  const digits = String(number ?? "").replace(/\D/g, "");
  if (digits.length >= 4) return digits.slice(-4);
  return "";
}

/** Keep Intuit-style mask, or build xxxxxxxxxxxx#### from last4. */
export function formatMaskedCardNumber(numberOrLast4) {
  const raw = String(numberOrLast4 ?? "").trim();
  if (!raw) return "";
  if (/x/i.test(raw) || /\*/.test(raw)) {
    return raw.replace(/\*/g, "x").toLowerCase();
  }
  const last4 = last4FromCardNumber(raw);
  if (!last4) return "";
  return `xxxxxxxxxxxx${last4}`;
}

export function normalizeCardsResponse(response) {
  if (response == null) {
    return [];
  }
  const raw = response.data ?? response;
  let list = [];

  if (Array.isArray(raw?.cards)) {
    list = raw.cards;
  } else if (Array.isArray(raw?.data?.cards)) {
    list = raw.data.cards;
  } else if (Array.isArray(raw)) {
    list = raw;
  } else if (Array.isArray(raw?.data)) {
    list = raw.data;
  } else if (
    raw &&
    typeof raw === "object" &&
    (raw.CardLast4 != null ||
      raw.last4 != null ||
      raw.Last4 != null ||
      raw.PaymentSourceID != null ||
      raw.SquareCardID != null ||
      raw.CardID != null ||
      raw.card_id != null ||
      raw.id != null ||
      raw.number != null ||
      raw.cardType != null)
  ) {
    list = [raw];
  }

  return list
    .filter((c) => c && typeof c === "object")
    .map((c) => {
      const maskedNumber =
        c.number ?? c.Number ?? c.card_number ?? c.CardNumber ?? "";
      const last4 =
        c.CardLast4 ??
        c.last4 ??
        c.Last4 ??
        c.last_four ??
        last4FromCardNumber(maskedNumber) ??
        "";
      const brand =
        c.CardBrand ?? c.cardType ?? c.brand ?? c.Brand ?? c.card_brand ?? "";
      const expMonth = c.ExpMonth ?? c.expMonth ?? c.exp_month ?? 0;
      const expYear = c.ExpYear ?? c.expYear ?? c.exp_year ?? 0;
      const isDefault = parseIsDefault(
        c.IsDefault ??
          c.is_default ??
          c.isDefault ??
          c.default ??
          c.is_default_card,
      );
      // Prefer Intuit card id for actions; keep dealer row id when present
      const cardId =
        c.CardID ??
        c.SquareCardID ??
        c.PaymentSourceID ??
        c.payment_source_id ??
        c.card_id ??
        c.id ??
        null;
      const rowId =
        c.DealerCardNo ??
        c.dealer_card_no ??
        c.CardNo ??
        c.card_no ??
        cardId ??
        c.pk ??
        null;
      return {
        DealerCardNo: rowId,
        DealerNo: c.DealerNo ?? c.dealer_no ?? c.dealer_id ?? null,
        SquareCardID: cardId != null ? String(cardId) : null,
        PaymentSourceID: cardId != null ? String(cardId) : null,
        SquareCustomerID:
          c.SquareCustomerID ??
          c.PaymentCustomerID ??
          c.CardCustomerID ??
          c.entityId ??
          c.square_customer_id ??
          c.customer_id ??
          null,
        CardHolderName:
          c.CardHolderName ??
          c.cardholder_name ??
          c.CardholderName ??
          c.name ??
          "",
        CardLast4: last4 != null && last4 !== "" ? String(last4) : "",
        CardNumberMasked: formatMaskedCardNumber(maskedNumber || last4),
        CardBrand: brand != null && brand !== "" ? String(brand) : "",
        ExpMonth: Number(expMonth) || 0,
        ExpYear: Number(expYear) || 0,
        IsDefault: isDefault ? 1 : 0,
        Status: c.status ?? c.Status ?? "",
      };
    });
}

export const AUTOBILLING_CARD_REQUIRED_MESSAGE =
  "At least one payment method must be added.";

/** Normalize AutoBilling from OEM dealers list (`auto_billing`: 0|1). */
export function normalizeAutoBilling(value) {
  if (value == null || value === "") return null;
  if (typeof value === "boolean") return value ? 1 : 0;
  const n = Number(value);
  if (Number.isFinite(n)) return n === 1 ? 1 : 0;
  const s = String(value).trim().toLowerCase();
  if (s === "yes" || s === "true") return 1;
  if (s === "no" || s === "false") return 0;
  return null;
}

function paymentApiBase() {
  const raw = process.env.REACT_APP_API_URL;
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s || s === "undefined") return "";
  return s.replace(/\/+$/, "");
}

function authHeadersFromSession() {
  const token = JSON.parse(sessionStorage.getItem("userData"));
  return { Authorization: `Bearer ${token?.access_token}` };
}

/** Returns true when the dealer has at least one saved card. */
export async function fetchDealerHasSavedCards(dealerNo) {
  if (dealerNo == null || dealerNo === "") return false;
  const base = paymentApiBase();
  const url = `${base}/api/dealers/${dealerNo}/cards/`;
  const { default: axios } = await import("axios");
  const res = await axios.get(url, { headers: authHeadersFromSession() });
  return normalizeCardsResponse(res.data).length > 0;
}

/**
 * Map gateway brand codes (MC, VI, AX, DISC, …) and names → icon key.
 */
export function brandKeyFromCard(card) {
  const raw = String(card?.CardBrand ?? "")
    .toLowerCase()
    .replace(/[_\s-]+/g, " ")
    .trim();
  if (!raw) return "generic";

  // Short codes from Intuit / processors
  if (raw === "mc" || raw === "m/c") return "mastercard";
  if (raw === "vi" || raw === "vs" || raw === "visa") return "visa";
  if (raw === "ax" || raw === "ae" || raw === "amex") return "amex";
  if (
    raw === "ds" ||
    raw === "di" ||
    raw === "dc" ||
    raw === "disc" ||
    raw === "discover"
  )
    return "discover";
  if (raw === "dn" || raw === "dr" || raw === "diners") return "diners";
  if (raw === "jc" || raw === "jcb") return "jcb";
  if (raw === "up" || raw === "cu") return "unionpay";

  if (raw.includes("visa")) return "visa";
  if (raw.includes("master")) return "mastercard";
  if (raw.includes("amex") || raw.includes("american express")) return "amex";
  if (raw.includes("disc") || raw.includes("discover")) return "discover";
  if (raw.includes("diner")) return "diners";
  if (raw.includes("jcb")) return "jcb";
  if (
    raw.includes("union") ||
    raw.includes("unionpay") ||
    raw.includes("china")
  )
    return "unionpay";
  return "generic";
}

/** Display label for list UI (MC → Mastercard). */
export function formatCardBrandLabel(brand) {
  const key = brandKeyFromCard({ CardBrand: brand });
  const labels = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "American Express",
    discover: "Discover",
    diners: "Diners Club",
    jcb: "JCB",
    unionpay: "UnionPay",
    generic: brand ? String(brand) : "Card",
  };
  return labels[key] || String(brand || "Card");
}

const CREDIT_CARD_TYPE_TO_BRAND = {
  visa: "visa",
  mastercard: "mastercard",
  "american-express": "amex",
  discover: "discover",
  "diners-club": "diners",
  jcb: "jcb",
  unionpay: "unionpay",
  maestro: "mastercard",
};

/**
 * Detect brand from PAN digits (credit-card-type) → our CardBrandIcon keys.
 */
export function brandKeyFromNumber(cardNumber) {
  const digits = String(cardNumber ?? "").replace(/\D/g, "");
  if (!digits) return "generic";
  const matches = creditCardType(digits);
  const type = matches?.[0]?.type;
  return CREDIT_CARD_TYPE_TO_BRAND[type] || "generic";
}

function getCardTypeInfo(cardNumber) {
  const digits = String(cardNumber ?? "").replace(/\D/g, "");
  if (!digits) return null;
  return creditCardType(digits)?.[0] || null;
}

/** Format PAN with brand-aware gaps (e.g. 4242 4242 4242 4242, Amex 3782 822463 10005). */
export function formatCardNumberInput(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  const info = getCardTypeInfo(digits);
  const gaps = info?.gaps?.length ? info.gaps : [4, 8, 12];
  const maxLen = info?.lengths?.length ? Math.max(...info.lengths) : 19;
  const truncated = digits.slice(0, maxLen);
  let out = "";
  for (let i = 0; i < truncated.length; i += 1) {
    if (gaps.includes(i)) out += " ";
    out += truncated[i];
  }
  return out;
}

/** CVV length for detected brand (Amex 4, most others 3). */
export function getCvvMaxLength(cardNumber) {
  const info = getCardTypeInfo(cardNumber);
  const size = Number(info?.code?.size);
  return Number.isFinite(size) && size > 0 ? size : 4;
}

export function formatExpShort(card) {
  if (!card?.ExpMonth || !card?.ExpYear) return "";
  const mm = String(card.ExpMonth).padStart(2, "0");
  const yy = String(card.ExpYear).slice(-2);
  return `Exp ${mm}/${yy}`;
}

export function formatExpiresOn(card) {
  if (!card?.ExpMonth || !card?.ExpYear) return "";
  const mm = String(card.ExpMonth).padStart(2, "0");
  const yy = String(card.ExpYear).slice(-2);
  return `Expires on ${mm}/${yy}`;
}

/** Card brand logos from src/assets/images/logo — same size for all. */
const CARD_BRAND_LOGOS = {
  visa: visaLogo,
  mastercard: mastercardLogo,
  amex: amexLogo,
  discover: discoverLogo,
  diners: dinersLogo,
  jcb: jcbLogo,
};

const CARD_BRAND_TITLES = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  discover: "Discover",
  diners: "Diners Club",
  jcb: "JCB",
  unionpay: "UnionPay",
  generic: "Card",
};

/** Fixed badge size so every brand logo matches in the list / form. */
export function CardBrandIcon({ brandKey, className = "shrink-0" }) {
  const k = brandKey || "generic";
  const src = CARD_BRAND_LOGOS[k];
  const title = CARD_BRAND_TITLES[k] || "Card";
  const wrap =
    "inline-flex h-[32px] w-[48px] items-center justify-center rounded-[3px] border border-[#e4e7ec] bg-white overflow-hidden " +
    className;

  if (src) {
    return (
      <span className={wrap} aria-hidden title={title}>
        <img
          src={src}
          alt=""
          className="h-full w-full object-contain"
          draggable={false}
        />
      </span>
    );
  }

  // Fallback when no asset (e.g. UnionPay / unknown)
  return (
    <span className={wrap} aria-hidden title={title}>
      <svg viewBox="0 0 48 32" className="h-full w-full" role="img">
        <rect width="48" height="32" fill="#F8FAFC" />
        <rect x="0" y="9" width="48" height="4" fill="#CBD5E1" />
        <rect x="6" y="20" width="12" height="3" rx="1" fill="#E2E8F0" />
        <rect x="30" y="20" width="12" height="3" rx="1" fill="#E2E8F0" />
      </svg>
    </span>
  );
}
